// Guest-side error reporting + friendly messages.
//
// Two jobs:
//   1. reportError(context, err) — fire-and-forget write to client_errors/{id}
//      so a guest's browser failure is visible server-side (Firestore console /
//      API) instead of vanishing on their phone. Never throws, never blocks the
//      UI. The security rules accept create-only with a tight field contract.
//   2. describeError(err) — map Firebase/network error codes to plain,
//      actionable guest text (never a bare "Something went wrong" that leaves
//      the guest with no next step and us with no signal).
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase.js";

// The villa line is the universal fallback action for a stuck guest.
export const SUPPORT_PHONE = "507-693-7825";

export function describeError(err) {
  const code = (err && (err.code || err.name)) || "";
  // Order matters: auth codes ("auth/network-request-failed") contain
  // "network" — match the specific classes before the transport ones.
  if (code.includes("unauthenticated") || code.includes("auth/")) {
    return "Your session expired. Reload the page and try again - if it keeps happening, text Julie at " + SUPPORT_PHONE + ".";
  }
  if (code.includes("permission-denied")) {
    return "This link can no longer be used. Ask Julie for a fresh one - call or text " + SUPPORT_PHONE + ".";
  }
  if (code.includes("unavailable") || code.includes("network") || code.includes("failed-fetch")) {
    return "Connection trouble on our end. Check you're online and try again - or text Julie at " + SUPPORT_PHONE + ".";
  }
  return "Something went wrong on our end. Please try again - if it keeps failing, text Julie at " + SUPPORT_PHONE + ".";
}

export function reportError(context, err, extra = {}) {
  try {
    // eslint-disable-next-line no-console
    console.error(`[obr] ${context}:`, err);
    const doc = {
      context: String(context || "unknown").slice(0, 60),
      code: String((err && (err.code || err.name)) || "").slice(0, 80),
      message: String((err && err.message) || err || "").slice(0, 500),
      url: String((typeof location !== "undefined" && location.href) || "").slice(0, 300),
      ua: String((typeof navigator !== "undefined" && navigator.userAgent) || "").slice(0, 300),
      created_at: serverTimestamp(),
    };
    // Fire-and-forget: a failed error report must never mask the real error.
    addDoc(collection(db, "client_errors"), doc).catch(() => {});
  } catch {
    /* reporting must never throw */
  }
}
